import type { ExperienceDescriptor } from "@visorxr/core";

export interface ShellCallbacks {
  onStart: () => void;
  onCapture: () => void;
}

type ShellState = "gate" | "loading" | "tracking" | "error";

/**
 * The Optics-styled UI shell: a permission gate ("Start AR"), a loading state, a capture FAB, and
 * an error panel. `root` doubles as the WebXR dom-overlay root. The shell only renders/transitions
 * UI — all engine work happens in the injected callbacks (SRP).
 */
export class UiShell {
  private readonly startButton: HTMLButtonElement;
  readonly root: HTMLElement;
  private readonly gate: HTMLElement;
  private readonly loading: HTMLElement;
  private readonly errorPanel: HTMLElement;
  private readonly errorMessage: HTMLElement;
  private readonly fab: HTMLButtonElement;
  private readonly unmute: HTMLButtonElement;
  private onUnmute: (() => void) | null = null;
  private state: ShellState | null = null;
  private readonly captureEnabled: boolean;

  constructor(parent: HTMLElement, descriptor: ExperienceDescriptor, callbacks: ShellCallbacks) {
    // A campaign can opt out of the capture affordance with branding.captureButton = false.
    this.captureEnabled = descriptor.branding?.captureButton !== false;
    this.root = el("div", "visor-shell");

    this.gate = el("div", "visor-glass visor-panel");
    // Disabled, not merely hidden: the handler reads backend ordering that only exists after run()
    // has awaited a dynamic import and a support probe, so reaching it early is a temporal-dead-zone
    // throw rather than a start. `hidden` alone leaves the button dispatchable.
    this.startButton = button("visor-btn", "Start AR", callbacks.onStart);
    this.startButton.disabled = true;
    this.gate.append(
      text("h1", "visor-title", descriptor.title),
      text("p", "visor-sub", "Point your camera at an open space, then start."),
      this.startButton,
    );

    this.gate.hidden = true;

    this.loading = el("div", "visor-glass visor-panel");
    this.loading.append(el("div", "visor-spinner"), text("p", "visor-sub", "Starting AR…"));
    this.loading.hidden = true;

    this.errorPanel = el("div", "visor-glass visor-panel visor-error");
    this.errorMessage = text("p", "visor-sub", "");
    this.errorPanel.append(text("h1", "visor-title", "Something went wrong"), this.errorMessage);
    this.errorPanel.hidden = true;

    this.fab = button("visor-fab", "", callbacks.onCapture);
    this.fab.setAttribute("aria-label", "Capture photo");
    this.fab.hidden = true;

    // One tap is the whole job — the platform remembers the gesture — so the button retires itself
    // rather than lingering over the overlay it just fixed. Retired is a *state*, not a display
    // accident: the handler is cleared, or the next state render would derive the button back into
    // view from a tap that already happened. The visible text is the accessible name: a differing
    // aria-label fails voice control ("Label in Name"), and this is not an icon-only control.
    this.unmute = button("visor-btn visor-unmute", "Turn on sound", () => {
      const onTap = this.onUnmute;
      this.onUnmute = null;
      this.unmute.hidden = true;
      onTap?.();
    });
    this.unmute.hidden = true;

    this.root.append(this.gate, this.loading, this.errorPanel, this.fab, this.unmute);
    parent.append(this.root);
  }

  private show(state: ShellState): void {
    this.state = state;
    this.gate.hidden = state !== "gate";
    this.loading.hidden = state !== "loading";
    this.errorPanel.hidden = state !== "error";
    this.fab.hidden = state !== "tracking" || !this.captureEnabled;
    this.unmute.hidden = !this.unmuteApplies();
  }

  /**
   * Derived from state and offer together, in every place either changes — the offer is the
   * backend's to make, but only the tracking state can honour it, and deriving is what keeps that
   * true under any ordering of the two rather than under the one main.ts happens to use today.
   */
  private unmuteApplies(): boolean {
    return this.state === "tracking" && this.onUnmute !== null;
  }

  showGate(): void {
    this.startButton.disabled = false;
    this.show("gate");
  }

  showLoading(message: string): void {
    // Start is single-flight: a second tap while the first attempt is in flight would race two
    // camera acquisitions and two sessions.
    this.startButton.disabled = true;
    const sub = this.loading.querySelector(".visor-sub");
    if (sub) sub.textContent = message;
    this.show("loading");
  }

  showTracking(): void {
    this.show("tracking");
  }

  showError(message: string): void {
    this.errorMessage.textContent = message;
    this.show("error");
  }

  setCapturing(capturing: boolean): void {
    this.fab.disabled = capturing;
  }

  /** Offers the unmute tap. The handler must do the platform work synchronously — the tap is the
   *  user gesture this affordance exists to capture. An offer made outside the tracking state is
   *  held, not shown: it becomes visible if and when tracking does. */
  showUnmute(onTap: () => void): void {
    this.onUnmute = onTap;
    this.unmute.hidden = !this.unmuteApplies();
  }

  hideUnmute(): void {
    this.onUnmute = null;
    this.unmute.hidden = true;
  }
}

function el(tag: string, className: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}

function text(tag: string, className: string, content: string): HTMLElement {
  const node = el(tag, className);
  node.textContent = content;
  return node;
}

function button(className: string, label: string, onClick: () => void): HTMLButtonElement {
  const node = document.createElement("button");
  node.className = className;
  node.type = "button";
  if (label) node.textContent = label;
  node.addEventListener("click", onClick);
  return node;
}
